/**
 * Consent storage utilities for GDPR compliance
 * Handles user consent for cookies, analytics, and marketing
 */

const CONSENT_STORAGE_KEY = "user-consent-preferences";
const CONSENT_VERSION = "2024.1";

/**
 * Default consent configuration
 */
export const DEFAULT_CONSENT = {
  necessary: true, // Always required
  analytics: false,
  marketing: false,
  functional: false,
  version: CONSENT_VERSION,
  timestamp: null,
};

/**
 * Gets stored user consent preferences
 * @returns {Object} Consent preferences object
 */
export const getStoredConsent = () => {
  if (typeof window === "undefined") return DEFAULT_CONSENT;

  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return DEFAULT_CONSENT;

    const consent = JSON.parse(stored);

    // Check if consent is current version
    if (!consent.version || consent.version !== CONSENT_VERSION) {
      return DEFAULT_CONSENT;
    }

    return {
      ...DEFAULT_CONSENT,
      ...consent,
    };
  } catch (error) {
    console.warn("Error reading consent preferences:", error);
    return DEFAULT_CONSENT;
  }
};

/**
 * Stores user consent preferences
 * @param {Object} consent - Consent preferences
 */
export const storeConsent = (consent) => {
  if (typeof window === "undefined") return;

  const consentToStore = {
    ...consent,
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };

  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentToStore));
  } catch (error) {
    console.warn("Error storing consent preferences:", error);
  }
};

/**
 * Checks if user has given consent for a specific category
 * @param {string} category - Consent category ('analytics', 'marketing', etc.)
 * @returns {boolean} Whether consent is granted
 */
export const hasConsent = (category) => {
  const consent = getStoredConsent();
  return consent[category] === true;
};

/**
 * Checks if user has provided any consent decision
 * @returns {boolean} Whether consent has been decided
 */
export const hasConsentDecision = () => {
  const consent = getStoredConsent();
  return consent.timestamp !== null;
};

/**
 * Clears all consent preferences
 */
export const clearConsent = () => {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch (error) {
    console.warn("Error clearing consent preferences:", error);
  }
};

/**
 * Updates a specific consent category
 * @param {string} category - Consent category
 * @param {boolean} granted - Whether consent is granted
 */
export const updateConsent = (category, granted) => {
  const currentConsent = getStoredConsent();
  const updatedConsent = {
    ...currentConsent,
    [category]: granted,
  };
  storeConsent(updatedConsent);
};

/**
 * Storage interface for backwards compatibility
 * Provides localStorage-like interface for legacy code
 */
export const storage = {
  getItem: (key) => {
    if (typeof window === "undefined") return null;
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn("Error getting item from storage:", error);
      return null;
    }
  },

  setItem: (key, value) => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn("Error setting item in storage:", error);
    }
  },

  removeItem: (key) => {
    if (typeof window === "undefined") return;
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn("Error removing item from storage:", error);
    }
  },
};
